import React from 'react';
import gql from 'graphql-tag';
import uuidv4 from 'uuid/v4';
import { toast } from 'react-toastify';
import swal from 'sweetalert';

import client from '../../../utils/apolloClient';

const searchTableBookingQuery = gql`
  query searchTableBookingQuery($input: SearchInput) {
    search_table_booking(input: $input) {
      total_pages
      total_size
      table_and_guest_counts {
        booking_count
        cancelled_booking_count
        guest_count
        seated_count
        booking_fee
      }
      table_booking_listing {
        restaurant_notes
        address_line_1
        address_line_2
        app_type
        booking_date
        booking_reference
        booking_status
        booking_time
        city
        cancelled {
          created_at
          created_by
        }
        confirmed_count
        country
        guest_count
        guest_notes
        guest_status
        image_url
        platform
        service_type
        service_type_setting_id
        state
        table_booking_id
        table_number
        tags
        user_id
        waitlisted {
          status
        }
        contact {
          contact_email
          contact_phone
          first_name
          last_name
        }
      }
    }
  }
`;

export const searchTableBookings = async ({
  adminUserId,
  placeId,
  userId = null,
  bookingDate = null,
  bookingStatus = null,
  tableBookingId = null,
  guestStatus = null,
  dateFilter = null,
  tags = null,
  from,
  size,
  sort = null,
  bookingFeeFilter = null,
}) => {
  const input = {
    user_id: adminUserId,
    size,
    from,
    filter: {
      place_filter: {
        place_id: placeId,
      },
      table_booking_filter: {},
    },
  };

  if (bookingFeeFilter) {
    input.filter.table_booking_filter.booking_fee = bookingFeeFilter;
  }

  if (tags) {
    input.filter.table_booking_filter.tags = tags;
  }

  if (dateFilter) {
    input.filter.table_booking_filter.booking_date = dateFilter;
  }

  if (userId) {
    input.filter.table_booking_filter.user_id = userId;
  }

  if (bookingDate) {
    input.filter.table_booking_filter.booking_date = bookingDate;
  }

  if (bookingStatus) {
    input.filter.table_booking_filter.booking_status = bookingStatus;
  }

  if (tableBookingId) {
    input.filter.table_booking_filter.table_booking_id = tableBookingId;
  }

  if (guestStatus) {
    input.filter.table_booking_filter.guest_status = guestStatus;
  }

  if (sort) {
    input.sort = sort;
  } else if (dateFilter) {
    input.sort = 'DATE_TIME_BY_ASC';
  } else if (bookingStatus === 'RECEIVED') {
    input.sort = 'CREATED_AT_DESC';
  } else {
    input.sort = 'DATE_TIME_BY_ASC';
  }

  try {
    const data = await client.clientPrivate.query({
      client: client.clientPrivate,
      query: searchTableBookingQuery,
      variables: {
        input,
      },
      fetchPolicy: 'network-only',
    });

    if (
      data &&
      data?.data?.search_table_booking &&
      Array.isArray(data.data.search_table_booking?.table_booking_listing) &&
      data.data.search_table_booking?.table_booking_listing.length !== 0
    ) {
      return data.data.search_table_booking;
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const useTableBookingsData = ({
  adminUserId,
  placeId,
  userId = null,
  bookingDate = null,
  bookingStatus = null,
  tableBookingId = null,
  guestStatus = null,
  dateFilter = null,
  from = 0,
  size = 10,
  tags = null,
  sort = null,
  bookingFeeFilter = null,
}) => {
  console.log('tag', tags);
  console.log('guestStatus', guestStatus);
  console.log('bookingStatus', bookingStatus);

  const [loading, setLoading] = React.useState(false);
  const [tableBookings, setTableBookings] = React.useState([]);
  const [pageCount, setPageCount] = React.useState(0);
  const [totalElements, setTotalElements] = React.useState(0);
  const [aggregates, setAggregates] = React.useState({});

  const tagsStr = tags.join('%>%');

  const [refresh, setRefresh] = React.useState(uuidv4());

  // React.useEffect(() => {
  //   const interval = setInterval(() => setRefresh(uuidv4()), [30000]);
  //   return () => clearInterval(interval);
  // }, []);

  React.useEffect(() => {
    const fetchFunc = async () => {
      setLoading(true);
      setTableBookings([]);
      setPageCount(0);
      setTotalElements(0);
      setAggregates({});
      if (adminUserId && placeId) {
        const res = await searchTableBookings({
          adminUserId,
          userId,
          placeId,
          bookingDate,
          bookingStatus,
          tableBookingId,
          guestStatus,
          dateFilter,
          from,
          size,
          tags:
            tagsStr.split('%>%').filter(ele => !!ele).length !== 0
              ? tagsStr.split('%>%')
              : null,
          sort,
          bookingFeeFilter,
        });

        if (res) {
          setTableBookings(res.table_booking_listing);
          setPageCount(res.total_pages);
          setTotalElements(res.total_size);
          setAggregates(res.table_and_guest_counts);
        } else {
          // pass
        }
      } else {
        // pass
      }

      setLoading(false);
    };
    fetchFunc();
  }, [
    tagsStr,
    userId,
    placeId,
    bookingDate,
    bookingStatus,
    tableBookingId,
    guestStatus,
    dateFilter,
    from,
    size,
    refresh,
    sort,
    bookingFeeFilter,
  ]);

  return [
    loading,
    tableBookings,
    pageCount,
    totalElements,
    aggregates,
    () => setRefresh(uuidv4()),
  ];
};

const searchTableBookingDetailsQuery = gql`
  query searchTableBookingDetailsQuery($input: SearchInput) {
    search_table_booking(input: $input) {
      total_size
      table_booking_listing {
        reverse_transfer
        refund_application_fee
        refund_amount
        payment {
          payment_state
          payment_at
          payment_card {
            brand
            country
            last4
          }
          refunds_details {
            amount
            attempted_by
            currency
            note
            reason
            refund_amount
            refund_reason
            refund_type
            refunded_at
            status
          }
          refund_disbursement {
            refund_impact
          }
        }
        billing {
          booking_total
          booking_value
          charge
          charges_list {
            is_kravein_charge
            is_payment_gateway_fee
            name
            value
          }
          kravein_charge
          payment_gateway_fee
          refund_amount
          sub_total
        }
        user_id
        restaurant_notes
        tags
        service_type_setting_id
        place_id
        platform
        guest_count
        guest_notes
        table_booking_id
        booking_date
        booking_time
        address_line_1
        address_line_2
        city
        state
        country
        post_code
        latitude
        longitude
        location
        service_type
        booking_reference
        booking_status
        guest_status
        image_url
        table_number
        audit {
          created_at
          created_by
          updated_at
          updated_by
        }
        cancelled {
          created_at
          created_by
          note {
            date
            type
            value
          }
          status
          user_data {
            email
            first_name
            last_name
          }
          user_type
        }
        confirmed {
          created_at
          created_by
          note {
            date
            type
            value
          }
          status
          user_data {
            email
            first_name
            last_name
          }
          user_type
        }
        contact {
          contact_email
          contact_phone
          first_name
          last_name
        }
        received {
          created_at
          created_by
          note {
            date
            type
            value
          }
          status
          user_type
          user_data {
            email
            first_name
            last_name
          }
        }
        waitlisted {
          created_at
          created_by
          note {
            date
            type
            value
          }
          status
          user_data {
            email
            first_name
            last_name
          }
          user_type
        }
      }
    }
  }
`;

export const getTableBookingsDetails = async ({
  adminUserId,
  tableBookingId,
}) => {
  const input = {
    user_id: adminUserId,
    filter: {
      table_booking_filter: {
        table_booking_id: tableBookingId,
      },
    },
  };

  try {
    const data = await client.clientPrivate.query({
      client: client.clientPrivate,
      query: searchTableBookingDetailsQuery,
      variables: {
        input,
      },
      fetchPolicy: 'network-only',
    });

    if (
      data &&
      data?.data?.search_table_booking &&
      Array.isArray(data.data.search_table_booking?.table_booking_listing) &&
      data.data.search_table_booking?.table_booking_listing.length !== 0
    ) {
      return data.data.search_table_booking.table_booking_listing[0];
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const useTableBookingsDetails = ({
  adminUserId,
  tableBookingId,
  reloadTableBookingData,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [tableBookingDetails, setTableBookingDetails] = React.useState(null);

  React.useEffect(() => {
    const fetchFunc = async () => {
      setLoading(true);
      setTableBookingDetails(null);
      if (adminUserId && tableBookingId) {
        const res = await getTableBookingsDetails({
          adminUserId,
          tableBookingId,
        });
        if (res) {
          setTableBookingDetails(res);
        } else {
          // pass
        }
      } else {
        // pass
      }
      setLoading(false);
    };

    fetchFunc();
  }, [adminUserId, tableBookingId, reloadTableBookingData]);

  return [
    loading,
    tableBookingDetails,
    async () => {
      setLoading(true);
      setTableBookingDetails(null);
      if (adminUserId && tableBookingId) {
        const res = await getTableBookingsDetails({
          adminUserId,
          tableBookingId,
        });
        if (res) {
          setTableBookingDetails(res);
        } else {
          // pass
        }
      } else {
        // pass
      }
      setLoading(false);
    },
  ];
};

const cancellTableBookingMutation = gql`
  mutation cancellTableBookingMutation(
    $tableBookingId: String
    $userId: String
  ) {
    update_table_booking(
      input: {
        booking_status: "CANCELLED"
        table_booking_id: $tableBookingId
        user_id: $userId
      }
    ) {
      error {
        code
        description
      }
      cancelled {
        created_at
        created_by
        note {
          date
          type
          value
        }
        status
        user_type
        user_data {
          email
          first_name
          last_name
        }
      }
    }
  }
`;

export const cancellTableBooking = async ({ adminUserId, tableBookingId }) => {
  try {
    const data = await client.clientPrivate.mutate({
      client: client.clientPrivate,
      mutation: cancellTableBookingMutation,
      variables: {
        userId: adminUserId,
        tableBookingId,
      },
    });

    if (
      data &&
      data?.data?.update_table_booking &&
      data.data.update_table_booking.error
    ) {
      return false;
    }
    return true;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export const useCancellTableBooking = ({ adminUserId, tableBookingId }) => {
  const [loading, setLoading] = React.useState(false);
  const mutateFnc = async () => {
    setLoading(true);
    const res = await cancellTableBooking({ adminUserId, tableBookingId });
    setLoading(false);
    return res;
  };
  return [loading, mutateFnc];
};

const searchTableBookingByTimeQuery = gql`
  query searchTableBookingByTimeQuery(
    $date: String
    $placeId: String
    $userId: String
  ) {
    search_table_booking_by_time(
      input: {
        filter: {
          table_booking_filter: { booking_date: $date, place_id: $placeId }
        }
        user_id: $userId
      }
    ) {
      table_booking_listing {
        time_slot
        total_guest_count
        booking_details {
          guest_count
          table_number
        }
      }
    }
  }
`;

export const searchTableBookingByTime = async ({ date, placeId, userId }) => {
  try {
    const data = await client.clientPrivate.query({
      client: client.clientPrivate,
      query: searchTableBookingByTimeQuery,
      variables: {
        date,
        placeId,
        userId,
      },
    });

    if (
      data &&
      data?.data?.search_table_booking_by_time?.table_booking_listing &&
      Array.isArray(
        data.data.search_table_booking_by_time.table_booking_listing,
      ) &&
      data.data.search_table_booking_by_time.table_booking_listing.length !== 0
    ) {
      return data.data.search_table_booking_by_time.table_booking_listing;
    }
    return null;
  } catch {
    return null;
  }
};

const transformBookingsArray = (arr, max) => {
  if (arr.length === max) {
    return arr.reverse();
  }

  return Array.from(
    { length: max },
    (value, index) => arr[index] || null,
  ).reverse();
};

export const useTableBookingByTimeSlotsData = ({ date, placeId, userId }) => {
  const [loading, setLoading] = React.useState(false);
  const [
    tableBookingByTimeSlotsData,
    setTableBookingByTimeSlotsData,
  ] = React.useState(null);

  React.useEffect(() => {
    const fetchFunc = async () => {
      setLoading(true);
      setTableBookingByTimeSlotsData(null);
      const res = await searchTableBookingByTime({ date, placeId, userId });
      if (res) {
        const temp = {};
        let max = 0;
        res.forEach(ele => {
          max = Math.max(max, ele.booking_details.length);
          temp[ele.time_slot] = {
            totalBookings: ele.total_guest_count,
            bookings: ele.booking_details.map(ele2 =>
              ele2.table_number
                ? `${ele2.table_number} (${ele2.guest_count})`
                : ele2.guest_count,
            ),
          };
        });

        const temp2 = {};
        Object.keys(temp).forEach(ele => {
          temp2[ele] = {
            ...temp[ele],
            bookings: transformBookingsArray(temp[ele].bookings, max),
          };
        });

        setTableBookingByTimeSlotsData({
          slots: temp2,
          label: Array.from({ length: max }, (_, index) => index + 1),
        });
      }
      setLoading(false);
    };

    fetchFunc();
  }, [date, placeId, userId]);

  return [loading, tableBookingByTimeSlotsData];
};

const fetchTableBookingSlotsQuery = gql`
  query fetchTableBookingSlots(
    $date: String!
    $day: String!
    $placeId: String!
    $serviceTypeSettingId: String!
  ) {
    fetch_table_booking_slots(
      input: {
        day: $day
        date: $date
        place_id: $placeId
        service_type_setting_id: $serviceTypeSettingId
      }
    ) {
      greeting
      occupied
      time
    }
  }
`;

const getSlots = ({ date, day, placeId, serviceTypeSettingId }) =>
  new Promise(resolve => {
    client.clientPublic
      .query({
        client: client.clientPublic,
        query: fetchTableBookingSlotsQuery,
        variables: {
          day,
          date,
          placeId,
          serviceTypeSettingId,
        },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        if (
          data?.fetch_table_booking_slots &&
          Array.isArray(data.fetch_table_booking_slots) &&
          data.fetch_table_booking_slots.length !== 0
        ) {
          resolve(data.fetch_table_booking_slots);
        } else {
          resolve([]);
        }
      })
      .catch(() => {
        resolve([]);
      });
  });

const generateTimeSlots = async ({
  date,
  day,
  placeId,
  serviceTypeSettingId,
}) => {
  const slts = await getSlots({
    client,
    date,
    placeId,
    day,
    serviceTypeSettingId,
  });
  return slts;
};

const toMorningSlots = (timeSlots, slotsStats) =>
  (timeSlots ?? [])
    .filter(ele => ele.greeting === 'Morning')
    .map(ele => ({
      time: ele.time,
      occupied: ele.occupied,
      stats: slotsStats[ele.time],
    }));

const toAfternoonSlots = (timeSlots, slotsStats) =>
  (timeSlots ?? [])
    .filter(ele => ele.greeting === 'Afternoon')
    .map(ele => ({
      time: ele.time,
      occupied: ele.occupied,
      stats: slotsStats[ele.time],
    }));

const toEveningSlots = (timeSlots, slotsStats) =>
  (timeSlots ?? [])
    .filter(ele => ele.greeting === 'Evening')
    .map(ele => ({
      time: ele.time,
      occupied: ele.occupied,
      stats: slotsStats[ele.time],
    }));

export const useDateSlots = ({
  shldStart = false,
  date1,
  date,
  day,
  placeId,
  serviceTypeSettingId,
  userId,
  refetchSlots,
}) => {
  const [slots, setSlots] = React.useState({
    morning: [],
    afternoon: [],
    evening: [],
    allSlots: [],
  });
  const [loading1, setLoading1] = React.useState(true);
  const [loading2, setLoading2] = React.useState(true);

  React.useEffect(() => {
    const fetchFunc1 = async () => {
      try {
        setLoading1(true);
        const res = await searchTableBookingByTime({
          date: date1,
          placeId,
          userId,
        });

        const temp = {};

        if (res && Array.isArray(res)) {
          res.forEach(ele => {
            temp[ele.time_slot] = {
              totalBookings: ele.booking_details.length,
              totalGuests: ele.total_guest_count,
            };
          });
          return temp;
        }

        return {};
      } catch {
        return {};
      }
    };

    const fetchFunc2 = async slotsStatsTemp => {
      try {
        setLoading2(true);
        setSlots({
          morning: [],
          afternoon: [],
          evening: [],
          allSlots: [],
        });

        const allSlots = await generateTimeSlots({
          date,
          day,
          placeId,
          serviceTypeSettingId,
        });

        if (allSlots?.length && allSlots.length !== 0) {
          const t1 = toMorningSlots(allSlots, slotsStatsTemp);
          const t2 = toAfternoonSlots(allSlots, slotsStatsTemp);
          const t3 = toEveningSlots(allSlots, slotsStatsTemp);
          const t = [...t1, ...t2, ...t3];

          setSlots({
            morning: t1,
            afternoon: t2,
            evening: t3,
            allSlots: t,
          });
        }
        setLoading1(false);
        setLoading2(false);
      } catch {
        setLoading1(false);
        setLoading2(false);
      }
    };

    const fetchFunc = async () => {
      const slotsStatsTemp = await fetchFunc1();
      await fetchFunc2(slotsStatsTemp);
    };

    if (shldStart) {
      fetchFunc();
    }
  }, [
    shldStart,
    date1,
    date,
    day,
    placeId,
    serviceTypeSettingId,
    userId,
    refetchSlots,
  ]);

  const loading = loading1 || loading2;

  return [loading, slots, setSlots];
};

const updateTableSlotsMutation = gql`
  mutation updateTableSlotsMutation($input: TableSettingInput) {
    update_table_setting(input: $input) {
      error {
        code
        description
      }
    }
  }
`;

export const updateTableSlots = async ({ input }) => {
  try {
    const res = await client.clientPrivate.mutate({
      client: client.clientPrivate,
      mutation: updateTableSlotsMutation,
      variables: {
        input,
      },
    });

    if (
      res &&
      Array.isArray(res?.data?.update_table_setting?.error) &&
      res.data.update_table_setting.error[0]
    ) {
      return {
        error: res.data.update_table_setting.error[0]?.description,
        isError: true,
      };
    }
    return {
      error: null,
      isError: false,
    };
  } catch (error) {
    return {
      error:
        "Oops! Something didn't work as expected. Please contact support if the issue persists.",
      isError: true,
    };
  }
};

export const useUpdateTableSlotsFunc = ({
  userId,
  serviceTypeSettingId,
  placeId,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [refetch, setRefetch] = React.useState(uuidv4());
  //   occupied_slots: { date: "2025-02-20", slots: ["01:00 PM", "02:00 PM"] }
  const func = async occupiedSlots => {
    setLoading(true);
    const input = {
      user_id: userId,
      service_type_setting_id: serviceTypeSettingId,
      service_type: 'Table booking',
      place_id: placeId,
      occupied_slots: occupiedSlots,
    };

    const res = await updateTableSlots({ input });

    if (res.isError) {
      toast.error(res.error);
    } else {
      setRefetch(uuidv4());
      swal('Successfully updated slot status!');
    }
    setLoading(false);
  };

  return [loading, refetch, func];
};

export default {};
